import { useTranslations } from "next-intl";
import { useState } from "react";

import { Icon } from "@zapier/zinnia-icons";
import { ServiceIcon, Typeahead, TypeaheadItem } from "@zapier/design-system";

import { useAppTypeahead } from "./useAppTypeahead";
import type { AppResultFragment } from "./useAppTypeahead.generated";

export type AppType = AppResultFragment;

const LOAD_MORE_KEY = "__load_more__";
const LOADING_KEY = "__loading__";

type AppFilterProps = {
  id?: string;
  /** Pass to render selected items outside of dropdown */
  children?: (props: {
    appIds: string[];
    selectedApps: AppType[];
  }) => React.ReactElement[];
  menuWidth?: string;
  onChange: (value: string | undefined) => void;
  value: string[] | undefined;
};

type AppItem = AppType & { __isLoadMore?: boolean };

function AppFilter(props: AppFilterProps) {
  const t = useTranslations("history-page.filters.app-filter");
  const tCommon = useTranslations("common");
  const ids = props.value || [];
  const [inputValue, setInputValue] = useState("");
  const {
    apps,
    isAppLoading,
    isLoadingMore,
    isSearchLoading,
    onSearch,
    onLoadMore,
    selectedApps,
  } = useAppTypeahead({ ids });

  const showLoadMore = Boolean(onLoadMore) && !isLoadingMore;

  const allItems: AppItem[] = [
    ...apps,
    ...(isLoadingMore
      ? [
          {
            id: LOADING_KEY,
            name: tCommon("loading"),
            __isLoadMore: true,
          } as AppItem,
        ]
      : showLoadMore
        ? [
            {
              id: LOAD_MORE_KEY,
              name: tCommon("load-more"),
              __isLoadMore: true,
            } as AppItem,
          ]
        : []),
  ];

  return (
    <>
      <Typeahead
        aria-label={t("search-aria-label")}
        iconLeft="navApps"
        isBusy={isSearchLoading || isAppLoading}
        id={props.id}
        inputValue={inputValue}
        items={allItems}
        menuTrigger="focus"
        menuWidth={props.menuWidth}
        onInputChange={(v) => {
          setInputValue(v);
          onSearch(v);
        }}
        onSelectionChange={(key) => {
          if (key === LOAD_MORE_KEY) {
            onLoadMore?.();
            return;
          }
          if (key === LOADING_KEY) return;
          const selected = apps.find((a) => a.id === key);
          props.onChange(selected?.currentImplementation?.id || undefined);
          setInputValue("");
        }}
        placeholder={t("placeholder")}
        selectedKey={null}
        size="medium"
      >
        {(dsItem) => {
          const item = dsItem as AppItem;
          return (
            <TypeaheadItem
              id={item.id}
              isDisabled={item.id === LOADING_KEY}
              textValue={item.name}
            >
              {item.__isLoadMore ? (
                item.name
              ) : (
                <>
                  <AppIcon item={item} />
                  {item.name}
                </>
              )}
            </TypeaheadItem>
          );
        }}
      </Typeahead>
      {props.children && props.children({ appIds: ids, selectedApps })}
    </>
  );
}

function AppIcon({ item }: { item: AppType }) {
  if (!item?.images) {
    return <Icon name="navApps" size={24} />;
  }
  return (
    <ServiceIcon
      aria-hidden={true}
      serviceName={item.name}
      size="small"
      src={item.images.url32X32}
    />
  );
}

export default AppFilter;
